import { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { MessageItem } from './MessageItem';
import { useAuthStore } from '../../store/useAuthStore';
import type { Message } from '../../hooks/queries/useMessaging';
import type { MessageAttachment } from '../../services/messaging/messageAttachmentService';
import type { MessageReaction } from '../../services/messaging/messageReactionService';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  onEdit: (id: string, currentBody: string) => void;
  onReply: (message: Message) => void;
  onTogglePin?: (message: Message) => void;
  dmOtherLastReadAt?: string | null;
  attachmentsByMessage?: Map<string, MessageAttachment[]>;
  onDeleteAttachment?: (att: MessageAttachment) => void;
  reactionsByMessage?: Map<string, MessageReaction[]>;
  onToggleReaction?: (messageId: string, emoji: string, hasReacted: boolean) => void;
  onJumpToMessage?: (messageId: string) => void;
  /** Scroll initial vers le premier message dont createdAt > cette date. Si absent → scroll bas. */
  initialUnreadSince?: string | null;
  /** Pagination vers le haut : appelé quand on approche du sommet de la liste. */
  onLoadOlder?: () => void;
  /** Un lot de messages plus anciens est en cours de chargement. */
  isLoadingOlder?: boolean;
  /** Reste-t-il des messages plus anciens à charger ? */
  hasMore?: boolean;
}

function formatDateSeparator(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function isSameAuthorRecent(prev: Message, curr: Message): boolean {
  if (prev.authorId !== curr.authorId) return false;
  const diffMs = new Date(curr.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return diffMs < 5 * 60 * 1000; // 5 minutes
}

export function MessageList({ messages, isLoading, onDelete, onEdit, onReply,
  onTogglePin, dmOtherLastReadAt, attachmentsByMessage, onDeleteAttachment, reactionsByMessage, onToggleReaction, onJumpToMessage, initialUnreadSince, onLoadOlder, isLoadingOlder, hasMore }: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const firstUnreadRef = useRef<HTMLDivElement>(null);
  const initialScrollDone = useRef(false);
  const currentId = useAuthStore((s) => s.collaborator?.id);

  // Pagination vers le haut : on distingue "ajout en bas" (nouveau message → scroll bas)
  // de "ajout en haut" (anciens messages préfixés → préserver la position visuelle).
  // On mémorise l'id du 1er message + le scrollHeight avant rendu pour compenser.
  const prevFirstId = useRef<string | null>(null);
  const prevScrollHeight = useRef(0);
  const pendingRestore = useRef(false);

  // Avant chaque rendu : si le 1er message a changé pour un PLUS ancien, c'est une
  // pagination vers le haut → on marquera la position à restaurer.
  const firstId = messages[0]?.id ?? null;
  if (prevFirstId.current !== null && firstId !== prevFirstId.current && scrollRef.current) {
    const stillPresent = messages.some((m) => m.id === prevFirstId.current);
    if (stillPresent) {
      // L'ancien 1er message existe toujours mais n'est plus en tête → on a préfixé.
      prevScrollHeight.current = scrollRef.current.scrollHeight;
      pendingRestore.current = true;
    }
  }
  prevFirstId.current = firstId;

  // Map id → message pour résoudre les replies (parent_id).
  const messagesById = useMemo(() => {
    const m = new Map<string, Message>();
    for (const msg of messages) m.set(msg.id, msg);
    return m;
  }, [messages]);

  // Premier message dont createdAt > initialUnreadSince ET pas écrit par l'utilisateur.
  const firstUnreadId = useMemo(() => {
    if (!initialUnreadSince || !currentId) return null;
    for (const m of messages) {
      if (m.authorId === currentId) continue;
      if (new Date(m.createdAt) > new Date(initialUnreadSince)) return m.id;
    }
    return null;
  }, [messages, initialUnreadSince, currentId]);

  // Scroll initial : vers le 1er non lu si présent, sinon vers le bas. Une seule fois.
  useEffect(() => {
    if (initialScrollDone.current || isLoading || messages.length === 0) return;
    if (firstUnreadId && firstUnreadRef.current) {
      firstUnreadRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }
    initialScrollDone.current = true;
  }, [isLoading, messages.length, firstUnreadId]);

  // Après un préfixe (pagination vers le haut) : restaurer la position visuelle en
  // compensant l'augmentation de scrollHeight. Doit s'exécuter en layout (avant peinture)
  // pour éviter tout saut visible. Ne touche jamais bottomRef → pas de scroll bas ici.
  useLayoutEffect(() => {
    if (pendingRestore.current && scrollRef.current) {
      const delta = scrollRef.current.scrollHeight - prevScrollHeight.current;
      scrollRef.current.scrollTop += delta;
      pendingRestore.current = false;
    }
  });

  // Scroll vers le bas UNIQUEMENT sur un vrai ajout en bas (le DERNIER message change).
  // On ne se base plus sur messages.length (qui change aussi lors d'un préfixe vers le
  // haut) : on compare l'id du dernier message. Préfixe → dernier id inchangé → pas de
  // scroll bas (la position est gérée par le useLayoutEffect ci-dessus).
  // prevLastId suit toujours le dernier id observé (même avant le scroll initial) pour ne
  // jamais interpréter le 1er rendu comme un "nouveau message".
  const lastId = messages[messages.length - 1]?.id ?? null;
  const prevLastId = useRef<string | null>(null);
  useEffect(() => {
    const isNewBottom =
      initialScrollDone.current &&
      prevLastId.current !== null &&
      lastId !== prevLastId.current;
    prevLastId.current = lastId;
    if (isNewBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastId]);

  // Détection du scroll proche du sommet → charger le lot précédent.
  function handleScroll() {
    const el = scrollRef.current;
    if (!el || !onLoadOlder || !hasMore || isLoadingOlder) return;
    if (el.scrollTop < 120) onLoadOlder();
  }

  // Un média (GIF/image) charge de façon asynchrone APRÈS le scroll bas : sa hauteur
  // passe de 0 à ~192px, ce qui décale le bas et donne l'impression que la vue
  // "remonte". On re-scrolle en bas quand un média finit de charger, mais seulement
  // si l'utilisateur était déjà proche du bas (sinon on ne le déloge pas de sa lecture).
  function handleMediaLoad() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 250) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cime-text-muted" />
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <p className="text-cime-text-muted text-sm">
          Aucun message pour l'instant. Soyez le premier à écrire !
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto pb-2">
      {isLoadingOlder && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-cime-text-muted" />
        </div>
      )}
      {messages.map((message, index) => {
        const prev = index > 0 ? messages[index - 1] : null;
        const showDateSeparator = !prev || !isSameDay(prev.createdAt, message.createdAt);
        const showAuthor = !prev || showDateSeparator || !isSameAuthorRecent(prev, message);

        const isFirstUnread = message.id === firstUnreadId;

        return (
          <div key={message.id}>
            {showDateSeparator && (
              <div className="flex items-center gap-3 px-4 my-4">
                <div className="flex-1 h-px bg-cime-border" />
                <span className="text-xs text-cime-text-muted font-medium whitespace-nowrap">
                  {formatDateSeparator(message.createdAt)}
                </span>
                <div className="flex-1 h-px bg-cime-border" />
              </div>
            )}
            {isFirstUnread && (
              <div ref={firstUnreadRef} className="flex items-center gap-3 px-4 my-2">
                <div className="flex-1 h-px bg-red-300" />
                <span className="text-[10px] uppercase tracking-wider font-semibold text-red-500">
                  Nouveau
                </span>
                <div className="flex-1 h-px bg-red-300" />
              </div>
            )}
            <MessageItem
              message={message}
              showAuthor={showAuthor}
              onDelete={onDelete}
              onEdit={onEdit}
              onReply={onReply}
              onTogglePin={onTogglePin}
              showReadReceipt={message.authorId === currentId}
              dmOtherLastReadAt={dmOtherLastReadAt ?? null}
              attachments={attachmentsByMessage?.get(message.id) ?? []}
              onDeleteAttachment={onDeleteAttachment}
              reactions={reactionsByMessage?.get(message.id) ?? []}
              onToggleReaction={onToggleReaction}
              parent={message.parentId ? messagesById.get(message.parentId) ?? null : null}
              onJumpToMessage={onJumpToMessage}
              onMediaLoad={handleMediaLoad}
            />
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
